import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Star, ShieldCheck, Quote } from 'lucide-react';
import { reviewsAPI } from '../services/api';
import { productPath } from '../services/productPath';
import { prodName } from '../services/catalogI18n';
import { useT } from '../services/i18n';
import GoogleBusinessBlock from './GoogleBusinessBlock';
import TopProductBadge from './TopProductBadge';

const Stars = ({ value }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${value}/5`}>
    {[1, 2, 3, 4, 5].map(i => <Star key={i} size={13} className={i <= value ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} />)}
  </span>
);

const ReviewCard = ({ r, lang, t }) => (
  <article className="snap-start shrink-0 w-[280px] sm:w-[320px] bg-white border border-gray-200 rounded-xl p-4 flex flex-col gap-3 hover:shadow-md transition-shadow" data-testid={`wall-review-${r.id}`}>
    <div className="flex items-center justify-between gap-2">
      <Stars value={r.rating} />
      {r.verified !== false ? <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700"><ShieldCheck size={12} /> {t('reviews.verified')}</span>
        : <span className="inline-flex items-center gap-1 text-[11px] font-medium text-sky-700"><ShieldCheck size={12} /> {t('reviews.merchantCollected')}</span>}
    </div>
    {r.photos?.length > 0 && (
      <Link to={productPath(r.product, `#avis-${r.id}`)} className="block">
        <img src={r.photos[0]} alt={r.title || r.comment.slice(0, 60)} loading="lazy" className="w-full h-36 object-cover rounded-lg bg-gray-100" data-testid={`wall-review-photo-${r.id}`} />
      </Link>
    )}
    <div className="flex-1">
      {r.title && <p className="text-sm font-semibold text-gray-900 line-clamp-1">{r.title}</p>}
      <p className="text-sm text-gray-700 line-clamp-4 mt-0.5">« {r.comment} »</p>
    </div>
    <div className="flex items-center gap-2 border-t border-gray-100 pt-3">
      {r.product?.image && <img src={r.product.image} alt="" className="w-9 h-9 object-contain bg-white border rounded" />}
      <div className="min-w-0 flex-1">
        <Link to={productPath(r.product, `#avis-${r.id}`)} className="text-xs font-medium text-[#1e3a5f] hover:text-orange-600 line-clamp-1" data-testid={`wall-review-product-${r.id}`}>{prodName(r.product, lang)}</Link>
        <TopProductBadge product={r.product} />
        <p className="text-[11px] text-gray-500">{r.user_name} · {new Date(r.created_at).toLocaleDateString(lang === 'en' ? 'en-GB' : lang, { day: 'numeric', month: 'short', year: 'numeric' })}</p>
      </div>
    </div>
  </article>
);

/** Home page: carousel of the best recent verified reviews (photos first) + global rating. */
const ReviewsWall = () => {
  const { t, lang } = useT();
  const [data, setData] = useState(null);
  const [google, setGoogle] = useState(null);
  const ref = useRef(null);
  useEffect(() => { reviewsAPI.wall().then(setData).catch(() => setData(null)); reviewsAPI.googleBusiness().then(setGoogle).catch(() => setGoogle(null)); }, []);
  const hasItems = data?.items?.length > 0;
  if (!hasItems && !(google && google.rating != null)) return null;
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * 340, behavior: 'smooth' });
  return (
    <section className="py-8 sm:py-12 bg-[#f6f7f9]" data-testid="reviews-wall">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-orange-600 inline-flex items-center gap-1.5"><Quote size={13} /> {t('home.wall.kicker')}</p>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">{t('home.wall.title')}</h2>
            {hasItems && (
              <p className="text-sm text-gray-600 mt-1 inline-flex items-center gap-2 flex-wrap" data-testid="reviews-wall-summary">
                <span className="font-bold text-gray-900 text-base">{data.average}/5</span>
                <Stars value={Math.round(data.average)} />
                <span>{t('home.wall.summary', { n: data.count, count: data.count })}</span>
                {data.with_photos > 0 && <span className="text-gray-400">· {t('reviews.withPhotos', { n: data.with_photos, count: data.with_photos })}</span>}
              </p>
            )}
          </div>
          {hasItems && <div className="hidden sm:flex gap-2">
            <button type="button" onClick={() => scroll(-1)} className="w-9 h-9 rounded-full border border-gray-300 bg-white flex items-center justify-center hover:bg-gray-50" aria-label="previous" data-testid="reviews-wall-prev"><ChevronLeft size={18} /></button>
            <button type="button" onClick={() => scroll(1)} className="w-9 h-9 rounded-full border border-gray-300 bg-white flex items-center justify-center hover:bg-gray-50" aria-label="next" data-testid="reviews-wall-next"><ChevronRight size={18} /></button>
          </div>}
        </div>
        <div className="flex flex-col lg:flex-row gap-4 items-stretch">
          <GoogleBusinessBlock data={google} />
          {hasItems && (
            <div ref={ref} className="flex-1 min-w-0 flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 scrollbar-hide" data-testid="reviews-wall-track">
              {data.items.map(r => <ReviewCard key={r.id} r={r} lang={lang} t={t} />)}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ReviewsWall;
